import { object, TypeOf, z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { RefObject, useEffect } from "react";
import { focusNext } from "../../../helpers";
import { useConversationContext } from "../../../context/ChatContext/contextConvo";
import { nebulaChatApi } from "../../../redux/services/nebulaChat";

export type LoadingType = 'loading' | 'error' | 'success' | undefined;

export const useChatGroupEditor = ({ closeEditor }: { closeEditor: () => void; }) => {
    const { conversation } = useConversationContext();

    const ChatGroupEditorSchema = object({
        group_name: z.string().min(1, 'Group Name is Required'),
        users_config: z.object({
            adding: z.array(z.string()),
            removing: z.array(z.string()),
        }),
        group_icon: z.union([z.instanceof(Blob), z.literal('')]).optional(),
    });

    const [updateChatMeta, metaApiData] = nebulaChatApi.endpoints.updateChannelN.useMutation();
    const [channelAction, actionApiData] = nebulaChatApi.endpoints.channelActionN.useMutation();

    const [updateAvatar, avatarPUTApiData] = nebulaChatApi.endpoints.putChannelAvatarN.useMutation();
    const [deleteAvatar, avatarDELApiData] = nebulaChatApi.endpoints.deleteChannelAvatarN.useMutation();

    type ChatGroupFormInput = TypeOf<typeof ChatGroupEditorSchema>;

    const formMethods = useForm<ChatGroupFormInput>({
        resolver: zodResolver(ChatGroupEditorSchema),
        defaultValues: {
            group_name: conversation.display_name,
            users_config: {
                adding: [],
                removing: [],
            }
        }
    });

    useEffect(() => {
        const apis = [metaApiData, actionApiData, avatarPUTApiData, avatarDELApiData];

        if (apis.some(a => a.isLoading)) return;
        if (apis.some(a => a.isError)) return;
        if (!apis.some(a => a.isSuccess)) return;

        closeEditor();
    }, [metaApiData.isLoading, metaApiData.isSuccess, metaApiData.isError,
    actionApiData.isLoading, actionApiData.isSuccess, actionApiData.isError,
    avatarPUTApiData.isLoading, avatarPUTApiData.isSuccess, avatarPUTApiData.isError,
    avatarDELApiData.isLoading, avatarDELApiData.isSuccess, avatarDELApiData.isError]);

    const editGroup = async (formData: ChatGroupFormInput) => {
        if (formData.group_icon !== undefined) {
            if (formData.group_icon instanceof Blob) {
                const arrayBuffer = await formData.group_icon.arrayBuffer();
                updateAvatar({
                    channel_uuid: conversation.uuid,
                    data: arrayBuffer
                });
            } else {
                deleteAvatar({
                    channel_uuid: conversation.uuid,
                });
            }
        }

        if (formData.users_config.removing.length > 0) {
            channelAction({
                action: 'remove_members',
                channel_uuid: conversation.uuid,
                users: formData.users_config.removing
            });
        }

        if (formData.users_config.adding.length > 0) {
            channelAction({
                action: 'invite_members',
                channel_uuid: conversation.uuid,
                users: formData.users_config.adding
            });
        }

        if (conversation.display_name !== formData.group_name) {
            updateChatMeta({
                channel_uuid: conversation.uuid,
                name: formData.group_name
            });
        }
    }

    return {
        loading: metaApiData.isLoading || actionApiData.isLoading || avatarPUTApiData.isLoading || avatarDELApiData.isLoading,
        formMethods,
        editGroup,
    }
}

export const handleMembersKeydown = ({
    showDropdown,
    setShowDropdown,
    inputRef,
}: {
    showDropdown: boolean;
    setShowDropdown: (val: boolean) => void;
    inputRef: RefObject<HTMLInputElement>;
}) => {
    const handleArrowKeys = (e: React.KeyboardEvent<HTMLElement>, before?: boolean) => {

        if (!showDropdown) {
            setShowDropdown(true)
        }

        focusNext({
            container: e.currentTarget,
            before,
        })
    }

    const ACTIONS = {
        ArrowRight: (e) => handleArrowKeys(e),
        ArrowLeft: (e) => handleArrowKeys(e, true),
        ArrowDown: (e) => handleArrowKeys(e),
        ArrowUp: (e) => handleArrowKeys(e, true),
        Escape: () => {
            if (showDropdown && inputRef.current) {
                setShowDropdown(false)
                inputRef.current.focus();
            }
        },
        Enter: (e) => e.target.click(),
        Space: (e) => e.target.click(),
    }

    return (e) => {
        if (
            e.target.nodeName === 'BUTTON'
            && e.key.length === 1
            && /[a-zA-Z$&+,:;=?@#|'<>.-^*()%!]/.test(e.key)
            && inputRef.current
        ) {
            inputRef.current.focus();
        }

        const handler = ACTIONS[e.key];
        if (handler) {
            e.preventDefault();
            handler(e);
        }
    };
}
